import React, { useRef, useEffect, useState, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

// Horizontal scroll container: mouse-wheel scrolls sideways, click-drag to pan,
// optional arrow buttons. Vertical scrollbar hidden (see .hscroll in index.css).
const HScroll = ({ children, className = '', arrows = false, ...rest }) => {
  const ref = useRef(null);
  const drag = useRef({ active: false, startX: 0, startLeft: 0, moved: false });
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const updateArrows = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onWheel = (e) => {
      if (el.scrollWidth <= el.clientWidth) return;
      // Use whichever axis the wheel/trackpad reports, then normalise line/page deltas to pixels.
      let delta = Math.abs(e.deltaY) >= Math.abs(e.deltaX) ? e.deltaY : e.deltaX;
      if (e.deltaMode === 1) delta *= 16;                 // lines -> px
      else if (e.deltaMode === 2) delta *= el.clientWidth; // pages -> px
      delta *= 2.2;                                        // high sensitivity
      el.scrollLeft += delta;
      e.preventDefault();
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    el.addEventListener('scroll', updateArrows, { passive: true });
    // Recompute once images load / on mount.
    const t = setTimeout(updateArrows, 100);
    window.addEventListener('resize', updateArrows);
    updateArrows();
    return () => {
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('scroll', updateArrows);
      window.removeEventListener('resize', updateArrows);
      clearTimeout(t);
    };
  }, [updateArrows]);

  const scrollByDir = (dir) => {
    const el = ref.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.max(el.clientWidth * 0.8, 260), behavior: 'smooth' });
  };

  const onPointerDown = (e) => {
    const el = ref.current;
    if (!el) return;
    drag.current = { active: true, startX: e.clientX, startLeft: el.scrollLeft, moved: false };
  };
  const onPointerMove = (e) => {
    if (!drag.current.active) return;
    const el = ref.current;
    const dx = e.clientX - drag.current.startX;
    if (Math.abs(dx) > 4) drag.current.moved = true;
    if (drag.current.moved) el.scrollLeft = drag.current.startLeft - dx;
  };
  const endDrag = () => { drag.current.active = false; };
  const onClickCapture = (e) => {
    if (drag.current.moved) { e.stopPropagation(); e.preventDefault(); drag.current.moved = false; }
  };

  const scroller = (
    <div
      ref={ref}
      className={`hscroll flex gap-4 md:gap-5 overflow-x-auto overscroll-x-contain cursor-grab active:cursor-grabbing ${className}`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onPointerLeave={endDrag}
      onClickCapture={onClickCapture}
      {...rest}
    >
      {children}
    </div>
  );

  if (!arrows) return scroller;

  return (
    <div className="relative group/hscroll">
      {scroller}
      {canLeft && (
        <button
          type="button"
          data-testid="hscroll-left"
          onClick={() => scrollByDir(-1)}
          className="hidden sm:flex absolute left-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-navy/80 hover:bg-ember border border-white/20 items-center justify-center text-white shadow-lg transition-colors"
          aria-label="Scroll left"
        >
          <ChevronLeft size={22} />
        </button>
      )}
      {canRight && (
        <button
          type="button"
          data-testid="hscroll-right"
          onClick={() => scrollByDir(1)}
          className="hidden sm:flex absolute right-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-navy/80 hover:bg-ember border border-white/20 items-center justify-center text-white shadow-lg transition-colors"
          aria-label="Scroll right"
        >
          <ChevronRight size={22} />
        </button>
      )}
    </div>
  );
};

export default HScroll;
